import { useState } from 'react';
import { AVAILABILITY, ENGINE_TYPES, STATUSES } from '../constants';
import type { AvailabilityKey, EngineTypeKey, StatusKey, PickupKey } from '../types';
import type { usePlanningFilters } from './usePlanningFilters';

const ALL_AVAILABILITY_KEYS = Object.keys(AVAILABILITY) as AvailabilityKey[];
const ALL_STATUS_KEYS = Object.keys(STATUSES) as StatusKey[];
const ALL_ENGINE_TYPE_KEYS = Object.keys(ENGINE_TYPES) as EngineTypeKey[];
const ALL_PICKUP_KEYS: PickupKey[] = ['confirmed', 'not_confirmed'];

function toggleFixedSet<T>(prev: Set<T>, key: T, allKeys: T[]): Set<T> {
  const isAll = allKeys.every((k) => prev.has(k));
  if (isAll) return new Set([key]);
  const next = new Set(prev);
  if (next.has(key)) {
    next.delete(key);
    if (next.size === 0) return new Set(allKeys);
  } else {
    next.add(key);
    if (allKeys.every((k) => next.has(k))) return new Set(allKeys);
  }
  return next;
}

function toggleDynSet(prev: Set<string>, id: string, allIds: string[]): Set<string> {
  if (prev.size === 0) return new Set([id]);
  const next = new Set(prev);
  if (next.has(id)) {
    next.delete(id);
    if (allIds.filter((sid) => next.has(sid)).length === 0) return new Set();
  } else {
    next.add(id);
    if (allIds.every((sid) => next.has(sid))) return new Set();
  }
  return next;
}

interface DraftFilters {
  availability: Set<AvailabilityKey>;
  status: Set<StatusKey>;
  engineType: Set<EngineTypeKey>;
  site: Set<string>;
  supplier: Set<string>;
  subcontractor: Set<string>;
  category: Set<string>;
  option: Set<string>;
  pickup: Set<PickupKey>;
}

type PlanningFilters = ReturnType<typeof usePlanningFilters>;

interface UseMobileFilterDraftReturn {
  draftFilters: DraftFilters | null;
  openMobileFilter: () => void;
  saveMobileFilter: () => void;
  closeMobileFilter: () => void;
  toggleDraftAvailability: (key: AvailabilityKey) => void;
  toggleDraftStatus: (key: StatusKey) => void;
  toggleDraftEngineType: (key: EngineTypeKey) => void;
  toggleDraftSite: (id: string) => void;
  toggleDraftSupplier: (id: string) => void;
  toggleDraftSubcontractor: (id: string) => void;
  toggleDraftCategory: (category: string) => void;
  toggleDraftOption: (key: string) => void;
  toggleDraftPickup: (key: PickupKey) => void;
}

export function useMobileFilterDraft(filters: PlanningFilters): UseMobileFilterDraftReturn {
  const [draftFilters, setDraftFilters] = useState<DraftFilters | null>(null);

  function openMobileFilter() {
    setDraftFilters({
      availability: new Set(filters.availabilityFilter),
      status: new Set(filters.statusFilter),
      engineType: new Set(filters.engineTypeFilter),
      site: new Set(filters.siteFilter),
      supplier: new Set(filters.supplierFilter),
      subcontractor: new Set(filters.subcontractorFilter),
      category: new Set(filters.categoryFilter),
      option: new Set(filters.optionsFilter),
      pickup: new Set(filters.pickupFilter),
    });
    filters.setFilterOpen(true);
  }

  function saveMobileFilter() {
    if (draftFilters) {
      filters.setAvailabilityFilter(draftFilters.availability);
      filters.setStatusFilter(draftFilters.status);
      filters.setEngineTypeFilter(draftFilters.engineType);
      filters.setSiteFilter(draftFilters.site);
      filters.setSupplierFilter(draftFilters.supplier);
      filters.setSubcontractorFilter(draftFilters.subcontractor);
      filters.setCategoryFilter(draftFilters.category);
      filters.setOptionsFilter(draftFilters.option);
      filters.setPickupFilter(draftFilters.pickup);
    }
    filters.setFilterOpen(false);
    setDraftFilters(null);
  }

  function closeMobileFilter() {
    filters.setFilterOpen(false);
    setDraftFilters(null);
  }

  function toggleDraftAvailability(key: AvailabilityKey) {
    setDraftFilters((prev) =>
      prev ? { ...prev, availability: toggleFixedSet(prev.availability, key, ALL_AVAILABILITY_KEYS) } : prev
    );
  }

  function toggleDraftStatus(key: StatusKey) {
    setDraftFilters((prev) =>
      prev ? { ...prev, status: toggleFixedSet(prev.status, key, ALL_STATUS_KEYS) } : prev
    );
  }

  function toggleDraftEngineType(key: EngineTypeKey) {
    setDraftFilters((prev) =>
      prev ? { ...prev, engineType: toggleFixedSet(prev.engineType, key, ALL_ENGINE_TYPE_KEYS) } : prev
    );
  }

  function toggleDraftSite(id: string) {
    setDraftFilters((prev) =>
      prev
        ? { ...prev, site: toggleDynSet(prev.site, id, filters.sortedSites.map((s) => s.id)) }
        : prev
    );
  }

  function toggleDraftSupplier(id: string) {
    setDraftFilters((prev) =>
      prev
        ? { ...prev, supplier: toggleDynSet(prev.supplier, id, filters.uniqueSuppliers.map((s) => s.id)) }
        : prev
    );
  }

  function toggleDraftSubcontractor(id: string) {
    setDraftFilters((prev) =>
      prev
        ? {
            ...prev,
            subcontractor: toggleDynSet(
              prev.subcontractor,
              id,
              filters.sortedSubcontractors.map((s) => s.id)
            ),
          }
        : prev
    );
  }

  function toggleDraftCategory(category: string) {
    setDraftFilters((prev) =>
      prev
        ? { ...prev, category: toggleDynSet(prev.category, category, filters.sortedCategories) }
        : prev
    );
  }

  function toggleDraftOption(key: string) {
    setDraftFilters((prev) =>
      prev
        ? { ...prev, option: toggleDynSet(prev.option, key, filters.sortedOptions.map((o) => o.key)) }
        : prev
    );
  }

  function toggleDraftPickup(key: PickupKey) {
    setDraftFilters((prev) =>
      prev ? { ...prev, pickup: toggleFixedSet(prev.pickup, key, ALL_PICKUP_KEYS) } : prev
    );
  }

  return {
    draftFilters,
    openMobileFilter,
    saveMobileFilter,
    closeMobileFilter,
    toggleDraftAvailability,
    toggleDraftStatus,
    toggleDraftEngineType,
    toggleDraftSite,
    toggleDraftSupplier,
    toggleDraftSubcontractor,
    toggleDraftCategory,
    toggleDraftOption,
    toggleDraftPickup,
  };
}
